<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from '@/composables/useVuexHelpers';
import PermissionLevel from '@/assets/js/enum/PermissionLevel';

interface MenuPosition {
  x: number;
  y: number;
}

interface Props {
  menuPosition: MenuPosition;
  doResponseRoundAnnotation?: boolean;
  activeAnnotationId?: string | null;
}

interface Emits {
  (e: 'addAnnotation', type: string, isResponse?: boolean): void;
  (e: 'menuHover', annotationId: string | null): void;
  (e: 'menuLeave', annotationId: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {
  doResponseRoundAnnotation: false,
  activeAnnotationId: null,
});

const emit = defineEmits<Emits>();

const store = useStore();

const coachingMode = computed(() => store.state.coachingMode);
const menuFadeOut = computed(() => store.state.menuFadeOut);
const permissionLevel = computed(() => store.getters.permissionLevel);

function handleAdd() {
  if (coachingMode.value) {
    const annotationType =
      permissionLevel.value === PermissionLevel.TEAM_LEAD ? 'instruction' : 'hostInstruction';
    emit('addAnnotation', annotationType);
  } else if (props.doResponseRoundAnnotation) {
    emit('addAnnotation', 'comment', true);
  } else {
    emit('addAnnotation', 'comment', false);
  }
}

function handleMenuHover() {
  emit('menuHover', props.activeAnnotationId);
}

function handleMenuLeave() {
  emit('menuLeave', props.activeAnnotationId);
}
</script>

<template>
  <div
    id="annotations-menu"
    class="annotations-menu"
    :class="{
      fadeOut: menuFadeOut,
    }"
    :style="{
      left: `calc(${menuPosition.x}px - 7em)`,
      top: `calc(${menuPosition.y}px - 7.5em)`,
    }"
    @mouseenter="handleMenuHover"
    @mouseleave="handleMenuLeave"
  >
    <img @click="handleAdd" src="/icons/bubblemenu.svg" />
  </div>
</template>

<style lang="scss" scoped>
.annotations-menu {
  position: absolute;
  z-index: 1000;

  img {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &.fadeOut {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
}
</style>
